import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { WaitlistModal } from "@/pages/landing/WaitlistModal";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function LocationsHub() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waitlist, setWaitlist] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/api/public/location-pages`)
      .then((r) => r.json())
      .then((d) => setLocations(d.locations || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useSeo({
    title: "Areas We Serve in Bengaluru — Book a Verified Purohith Near You | PurohithConnect",
    description: "PurohithConnect brings KYC-verified Hindu purohiths to families across Bengaluru — Whitefield, Jayanagar, Malleshwaram, HSR Layout, Indiranagar, Yelahanka and more. Find a purohith near you for every sacred occasion.",
    canonicalPath: "/locations",
    jsonLd: useMemo(() => ([
      {
        "@context": "https://schema.org", "@type": "CollectionPage",
        name: "Areas We Serve — PurohithConnect Bengaluru",
        url: "https://purohithconnect.com/locations",
      },
      {
        "@context": "https://schema.org", "@type": "ItemList",
        itemListElement: locations.map((l, i) => ({
          "@type": "ListItem", position: i + 1, name: l.title,
          url: `https://purohithconnect.com/locations/${l.slug}`,
        })),
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://purohithconnect.com/" },
          { "@type": "ListItem", position: 2, name: "Areas We Serve", item: "https://purohithconnect.com/locations" },
        ],
      },
    ]), [locations]),
  });

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader openWaitlist={() => setWaitlist(true)} />
      <main>
        <section className="mx-auto max-w-7xl px-4 pt-12 md:px-8">
          <p className="font-mukta text-sm font-semibold uppercase tracking-widest text-[#A8722A]">Across Bengaluru</p>
          <h1 className="mt-2 font-eczar text-4xl font-bold leading-tight sm:text-5xl" data-testid="locations-title">
            A verified purohith <span className="text-[#B23A2B]">near you</span>
          </h1>
          <p className="mt-1 font-baloo text-lg text-[#A8722A]">ನಿಮ್ಮ ಪ್ರದೇಶದಲ್ಲಿ ಪುರೋಹಿತರು</p>
          <p className="mt-4 max-w-2xl font-mukta text-base text-[#6B5044] md:text-lg">
            Choose your area to see how PurohithConnect serves families there — KYC-verified purohiths of your tradition and language, for every ceremony, pooja and homa, with clear itemised summaries and no hidden charges.
          </p>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-12 md:px-8">
          {loading ? (
            <p className="font-mukta text-[#6B5044]" data-testid="locations-loading">Loading areas…</p>
          ) : locations.length === 0 ? (
            <p className="font-mukta text-[#6B5044]" data-testid="locations-empty">Area pages are coming soon.</p>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="locations-grid">
              {locations.map((l) => (
                <Link key={l.slug} to={`/locations/${l.slug}`} data-testid={`location-card-${l.slug}`}
                  className="group rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm transition-colors hover:border-[#B23A2B]">
                  <div className="flex items-center gap-2 text-[#B23A2B]">
                    <MapPin size={18} />
                    <h2 className="font-eczar text-xl font-semibold text-[#3B221B]">{l.area}</h2>
                  </div>
                  <p className="mt-2 line-clamp-3 font-mukta text-sm text-[#6B5044]">{l.intro}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-mukta text-sm font-semibold text-[#A8722A] group-hover:gap-2.5 transition-all">
                    Purohiths in {l.area} <ArrowRight size={15} />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
      <WaitlistModal key={waitlist} open={waitlist} defaultRole="Family" onClose={() => setWaitlist(false)} />
    </div>
  );
}
